import { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Spinner,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
} from '@pnl/ui';

interface AddonItem {
  id: string;
  code: string;
  marketCode: 'B3' | 'FOREX' | null;
  title: string;
  description: string;
  priceCents: number;
  currency: 'BRL' | 'USD';
  active: boolean;
  sortOrder: number;
  createdAt: string;
}

interface AddonFormState {
  code: string;
  title: string;
  description: string;
  priceCents: string; // input controlado em string
  currency: 'BRL' | 'USD';
  marketCode: '' | 'B3' | 'FOREX';
  sortOrder: string;
  active: boolean;
}

const EMPTY_FORM: AddonFormState = {
  code: '',
  title: '',
  description: '',
  priceCents: '0',
  currency: 'BRL',
  marketCode: '',
  sortOrder: '0',
  active: true,
};

const baseUrl = import.meta.env['VITE_API_URL'] ?? 'http://localhost:4000';

function authHeader(): HeadersInit {
  const token = localStorage.getItem('pnl_admin_access');
  return { Authorization: `Bearer ${token ?? ''}` };
}

async function fetchAddons(): Promise<AddonItem[]> {
  const res = await fetch(`${baseUrl}/admin/checkout-addons`, { headers: authHeader() });
  if (!res.ok) throw new Error(`Falha ao listar (HTTP ${res.status})`);
  const data = (await res.json()) as { items: AddonItem[] };
  return data.items;
}

async function saveAddon(input: AddonFormState, id?: string): Promise<void> {
  const url = id ? `${baseUrl}/admin/checkout-addons/${id}` : `${baseUrl}/admin/checkout-addons`;
  const body = {
    code: input.code,
    title: input.title,
    description: input.description,
    priceCents: Number(input.priceCents),
    currency: input.currency,
    marketCode: input.marketCode || null,
    sortOrder: Number(input.sortOrder),
    active: input.active,
  };
  const res = await fetch(url, {
    method: id ? 'PATCH' : 'POST',
    headers: { ...authHeader(), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: { message?: string } };
    throw new Error(err.error?.message ?? `Falha ao salvar (HTTP ${res.status})`);
  }
}

async function deleteAddon(id: string): Promise<void> {
  const res = await fetch(`${baseUrl}/admin/checkout-addons/${id}`, {
    method: 'DELETE',
    headers: authHeader(),
  });
  if (!res.ok) throw new Error(`Falha ao excluir (HTTP ${res.status})`);
}

function formatPrice(cents: number, currency: 'BRL' | 'USD'): string {
  const value = cents / 100;
  return value.toLocaleString(currency === 'BRL' ? 'pt-BR' : 'en-US', {
    style: 'currency',
    currency,
  });
}

export function CheckoutAddonsPage() {
  const [items, setItems] = useState<AddonItem[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<AddonItem | null>(null);

  async function refresh(): Promise<void> {
    setLoading(true);
    try {
      const addons = await fetchAddons();
      setItems(addons);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function handleDelete(id: string): Promise<void> {
    if (!confirm('Excluir este addon? Não afeta compras já realizadas.')) return;
    try {
      await deleteAddon(id);
      await refresh();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Erro ao excluir');
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="text-2xs text-brand-500 font-semibold uppercase tracking-widest">
            Catálogo
          </p>
          <h1 className="font-display text-ink-900 mt-1 text-3xl font-semibold tracking-tight">
            Produtos Extras
          </h1>
          <p className="text-ink-500 mt-1 text-sm">
            Addons opcionais do checkout (Saque Diário, Day Off, Boost, Restart, etc). Use pra
            campanhas promocionais — basta criar aqui e aparece no checkout automaticamente.
          </p>
        </div>
        <Button leadingIcon={<Plus size={14} />} onClick={() => setCreating(true)}>
          Novo produto extra
        </Button>
      </div>

      {error && <Alert tone="danger">{error}</Alert>}

      <Card className="overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Spinner size={24} />
          </div>
        ) : items && items.length > 0 ? (
          <Table>
            <THead>
              <TR>
                <TH>Código</TH>
                <TH>Título</TH>
                <TH>Mercado</TH>
                <TH className="text-right">Preço</TH>
                <TH>Status</TH>
                <TH className="text-right">Ações</TH>
              </TR>
            </THead>
            <TBody>
              {items.map((a) => (
                <TR key={a.id}>
                  <TD>
                    <code className="text-xs">{a.code}</code>
                  </TD>
                  <TD>{a.title}</TD>
                  <TD>{a.marketCode ?? 'Todos'}</TD>
                  <TD className="text-right tabular-nums">
                    {formatPrice(a.priceCents, a.currency)}
                  </TD>
                  <TD>
                    <Badge tone={a.active ? 'success' : 'neutral'}>
                      {a.active ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </TD>
                  <TD className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => setEditing(a)}>
                      Editar
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      leadingIcon={<Trash2 size={12} />}
                      onClick={() => handleDelete(a.id)}
                    >
                      Excluir
                    </Button>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        ) : (
          <EmptyState
            title="Nenhum produto extra cadastrado"
            description="Crie o primeiro produto extra pra usar em campanhas do checkout."
          />
        )}
      </Card>

      {(creating || editing) && (
        <AddonForm
          initial={editing ?? null}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={async () => {
            setCreating(false);
            setEditing(null);
            await refresh();
          }}
        />
      )}
    </div>
  );
}

function AddonForm({
  initial,
  onClose,
  onSaved,
}: {
  initial: AddonItem | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [form, setForm] = useState<AddonFormState>(
    initial
      ? {
          code: initial.code,
          title: initial.title,
          description: initial.description,
          priceCents: String(initial.priceCents),
          currency: initial.currency,
          marketCode: initial.marketCode ?? '',
          sortOrder: String(initial.sortOrder),
          active: initial.active,
        }
      : EMPTY_FORM,
  );
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setSubmitting(true);
    setErr(null);
    try {
      await saveAddon(form, initial?.id);
      await onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Erro');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 px-4 py-10">
      <form
        onSubmit={handleSubmit}
        className="bg-surface w-full max-w-xl space-y-4 rounded-2xl p-6 shadow-xl"
      >
        <header className="flex items-center justify-between">
          <h2 className="text-ink-900 text-xl font-semibold">
            {initial ? 'Editar produto extra' : 'Novo produto extra'}
          </h2>
          <button type="button" onClick={onClose} className="text-ink-500 hover:text-ink-900">
            ✕
          </button>
        </header>

        {err && <Alert tone="danger">{err}</Alert>}

        <Field label="Código (identificador único, ex: day-off)" required>
          <Input
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value })}
            placeholder="day-off"
            required
          />
        </Field>

        <Field label="Título exibido no checkout" required>
          <Input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="DAY OFF"
            required
          />
        </Field>

        <Field label="Descrição" required>
          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Esqueça um dia ruim durante seu exame…"
            className="border-ink-200 focus:border-brand-500 focus:ring-brand-500/20 w-full rounded-lg border bg-white px-3 py-2 text-sm outline-none focus:ring-2"
            rows={3}
            required
          />
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Preço (em centavos)" required>
            <Input
              type="number"
              min={0}
              value={form.priceCents}
              onChange={(e) => setForm({ ...form, priceCents: e.target.value })}
              required
            />
          </Field>
          <Field label="Moeda" required>
            <select
              value={form.currency}
              onChange={(e) => setForm({ ...form, currency: e.target.value as 'BRL' | 'USD' })}
              className="border-ink-200 focus:border-brand-500 focus:ring-brand-500/20 w-full rounded-lg border bg-white px-3 py-2 text-sm outline-none focus:ring-2"
            >
              <option value="BRL">BRL</option>
              <option value="USD">USD</option>
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Mercado (vazio = todos)">
            <select
              value={form.marketCode}
              onChange={(e) =>
                setForm({ ...form, marketCode: e.target.value as '' | 'B3' | 'FOREX' })
              }
              className="border-ink-200 focus:border-brand-500 focus:ring-brand-500/20 w-full rounded-lg border bg-white px-3 py-2 text-sm outline-none focus:ring-2"
            >
              <option value="">Todos</option>
              <option value="B3">B3</option>
              <option value="FOREX">FOREX</option>
            </select>
          </Field>
          <Field label="Ordem (menor primeiro)">
            <Input
              type="number"
              value={form.sortOrder}
              onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
            />
          </Field>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.active}
            onChange={(e) => setForm({ ...form, active: e.target.checked })}
          />
          Ativo no checkout
        </label>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
      </form>
    </div>
  );
}
